import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { BizouLogo } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";

export default function Invoices({ user }) {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const dateLocale = lang === "fr" ? frLocale : undefined;
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/invoices`, { credentials: "include" })
      .then(res => res.ok ? res.json() : [])
      .then(data => setInvoices(data || []))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (inv) => {
    const url = `${API}/invoices/${inv.invoice_id}/download`;
    // Open in a new tab — backend streams the PDF
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fmtAmount = (ttc, currency) => {
    const amount = parseFloat(ttc || 0).toFixed(2).replace(".", ",");
    return `${amount} ${(currency || "eur").toUpperCase()}`;
  };

  return (
    <div className="min-h-screen bg-[#FDF8F4]" data-testid="invoices-page">
      <header className="bg-white border-b border-[#F0E8EB]">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between h-16">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917]" data-testid="invoices-back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden md:inline text-sm">{t("common.back")}</span>
          </button>
          <a href="/dashboard"><BizouLogo size="md" /></a>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 md:py-14">
        <h1 className="text-3xl md:text-4xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t("invoices.title")}
        </h1>
        <p className="text-[#78716C] mb-8">{t("invoices.subtitle")}</p>

        {loading && (
          <div className="flex items-center gap-2 text-[#78716C]" data-testid="invoices-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
          </div>
        )}

        {!loading && invoices.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#F0E8EB] p-10 text-center" data-testid="invoices-empty">
            <FileText className="w-10 h-10 text-[#78716C]/40 mx-auto mb-3" />
            <p className="text-[#1C1917] font-medium mb-1">{t("invoices.emptyTitle")}</p>
            <p className="text-sm text-[#78716C]">{t("invoices.emptyDesc")}</p>
          </div>
        )}

        {!loading && invoices.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#F0E8EB] overflow-hidden" data-testid="invoices-list">
            {invoices.map((inv, idx) => (
              <div
                key={inv.invoice_id}
                data-testid={`invoice-row-${inv.invoice_number}`}
                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 ${idx < invoices.length - 1 ? "border-b border-[#F0E8EB]" : ""}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#FDF0F4] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[#E8175D]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[#1C1917] font-medium text-sm">{inv.invoice_number}</p>
                    <p className="text-xs text-[#78716C]">
                      {inv.issued_at && format(new Date(inv.issued_at), lang === "fr" ? "d MMMM yyyy" : "MMMM d, yyyy", { locale: dateLocale })}
                      {" · "}
                      {fmtAmount(inv.amount_ttc, inv.currency)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {inv.email_sent && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#0F7A4F]" data-testid={`invoice-sent-${inv.invoice_number}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t("invoices.emailSent")}
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(inv)}
                    className="rounded-full border-[#E8175D]/30 text-[#E8175D] hover:bg-[#FDF0F4] hover:border-[#E8175D]"
                    data-testid={`invoice-download-${inv.invoice_number}`}
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    {t("invoices.download")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-[#78716C] mt-6 text-center">
          {t("invoices.legalNotice")}
        </p>
      </main>
    </div>
  );
}
